/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import {
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { styles } from './ReportsScreen.styles';
import { spacing } from '../../shared/theme/spacing';
import { DATE_RANGE_PRESET_LABELS } from './reportsScreen.constants';
import type { ReportsScreenViewModel } from './useReportsScreen';
import {
  computeFullProfitLoss,
  formatReportsVentasDataSources,
  getReportsDateRange,
  isSalesTimeBandsApplicable,
} from './reportsScreen.utils';
import { ReportsSalesDailyBars } from './components/ReportsSalesDailyBars';
import { ReportsMarginDailyBars } from './components/ReportsMarginDailyBars';
import { ReportsPaymentMixDonut } from './components/ReportsPaymentMixDonut';
import { ReportsSalesByProductSection } from './components/ReportsSalesByProductSection';
import { ReportsBlockHeading } from './components/ReportsBlockHeading';
import { ReportsDataSourceFooter } from './components/ReportsDataSourceFooter';
import { PaperSheet } from '../../shared/ticket/PaperSheet';
import { ReportsScopeFiltersPanel } from './components/ReportsScopeFiltersPanel';
import { useReportsVentasLayout } from './reportsVentasLayout';
import { ReportsMetricTile } from './components/ReportsMetricTile';
import { ReportsErrorRetryCard } from './components/ReportsErrorRetryCard';
import { useGlobalFeatureFlags } from '../../shell/providers/GlobalFeatureFlagsProvider';
import { isGlobalFeatureOn } from '../../shell/feature-flags/globalFeatureFlagsService';
import { ticketColors } from '../../shared/theme/ticketColors';
import { formatMoney } from '../../shared/format/public-api';

function safeDeltaPct(current: number, previous: number): number | null {
  if (!Number.isFinite(previous) || previous === 0) return null;
  return (current - previous) / previous;
}

function formatDelta(pct: number | null): string {
  if (pct == null) return '';
  const sign = pct >= 0 ? '\u2191' : '\u2193';
  return ` \u00B7 ${sign} ${Math.abs(pct * 100).toFixed(1)}% vs anterior`;
}

function previousRangeLabelForPreset(preset: keyof typeof DATE_RANGE_PRESET_LABELS): string {
  if (preset === 'today') return 'Ayer';
  if (preset === 'yesterday') return 'Anteayer';
  if (preset === 'week') return 'Semana pasada';
  if (preset === 'last_7_days') return '7 días anteriores';
  if (preset === 'last_30_days') return '30 días anteriores';
  if (preset === 'this_month') return 'Mes anterior';
  if (preset === 'last_month') return 'Mes previo';
  if (preset === 'last_12_months') return '12 meses anteriores';
  return 'Rango inmediatamente anterior';
}

export function ReportsVentasView({ vm }: { vm: ReportsScreenViewModel }) {
  const ventasLayout = useReportsVentasLayout();
  const { layoutWide } = ventasLayout;
  const periodRange = getReportsDateRange(
    new Date(),
    vm.dateRangePreset,
    vm.dateRangePreset === 'custom' ? { from: vm.customRangeFrom, to: vm.customRangeTo } : null
  );
  const bandsSingleDay = isSalesTimeBandsApplicable(periodRange);
  const ticketPromedio =
    (vm.periodSalesSnapshot?.salesCount ?? 0) > 0
      ? (vm.periodSalesSnapshot?.salesTotal ?? 0) / (vm.periodSalesSnapshot?.salesCount ?? 1)
      : 0;

  const periodDataLoading = vm.loading;
  const fromMs = new Date(periodRange.fromIso).getTime();
  const toMs = new Date(periodRange.toIso).getTime();
  const spanDays = Math.floor((toMs - fromMs) / 86400000);
  const daysInRange = Math.max(1, spanDays + 1);
  const previousLabel = previousRangeLabelForPreset(vm.dateRangePreset);
  const currentSales = vm.periodSalesSnapshot?.salesTotal ?? 0;
  const previousSales = vm.previousPeriodSalesSnapshot?.salesTotal ?? 0;
  const salesDelta = currentSales - previousSales;
  const salesDeltaPct = safeDeltaPct(currentSales, previousSales);
  const currentSalesPerDay = currentSales / daysInRange;
  const previousSalesPerDay = previousSales / daysInRange;
  const currentMargin = vm.periodRentabilidadKpis?.rentabilidad.margenReposicionTotal ?? 0;
  const previousMargin = vm.previousPeriodRentabilidadKpis?.rentabilidad.margenReposicionTotal ?? 0;
  const marginDelta = currentMargin - previousMargin;
  const marginDeltaPct = safeDeltaPct(currentMargin, previousMargin);
  /** Monto pendiente con el mismo alcance que facturación (snapshot). Fallback a pos sólo sin filtro ítem y sin monto en RPC. */
  const snap = vm.periodSalesSnapshot;
  const snapPendingAmt =
    snap && typeof snap.pendingPaymentsAmount === 'number' ? snap.pendingPaymentsAmount : undefined;
  const pendingAmount =
    snapPendingAmt !== undefined
      ? snapPendingAmt
      : !vm.reportsQueryFiltersActive
        ? (vm.posExceptionSummary?.paymentPendingAmount ?? 0)
        : 0;
  const collectedAmount = Math.max(0, currentSales - pendingAmount);
  const periodSalesCount = vm.periodSalesSnapshot?.salesCount ?? 0;
  const pendingPaymentsCount = vm.periodSalesSnapshot?.pendingPayments ?? 0;
  const ventasSourceHint = formatReportsVentasDataSources(vm.kpiSources, null, {
    salesTimeBandsApplicable: bandsSingleDay,
  });

  const paymentBreakdown = vm.periodSalesSnapshot?.paymentBreakdown ?? [];
  const discountTotal = vm.periodSalesSnapshot?.discountTotal ?? 0;
  const subtotalBeforeDiscount =
    vm.periodSalesSnapshot?.subtotalBeforeDiscount ?? vm.periodSalesSnapshot?.salesTotal ?? 0;
  const salesWithDiscountCount =
    vm.periodSalesSnapshot?.salesWithDiscountCount ?? vm.promotionPerformance?.salesWithDiscountCount ?? 0;
  const salesWithoutDiscountCount = Math.max(
    0,
    (vm.periodSalesSnapshot?.salesCount ?? 0) - salesWithDiscountCount
  );
  const discountRate = subtotalBeforeDiscount > 0 ? discountTotal / subtotalBeforeDiscount : 0;
  const avgDiscountPerSaleWithPromo =
    salesWithDiscountCount > 0 ? discountTotal / salesWithDiscountCount : 0;
  const tileLayout = layoutWide ? 'wide' : 'default';
  const tileLayoutAdaptive = layoutWide ? 'adaptiveWide' : 'default';

  const { flags } = useGlobalFeatureFlags();
  const expensesModuleEnabled = isGlobalFeatureOn(flags, 'expenses_module');
  const profitLoss = vm.profitLoss;
  const fullPl = computeFullProfitLoss(profitLoss, vm.periodRentabilidadKpis?.rentabilidad);
  const prevPl = computeFullProfitLoss(
    vm.previousPeriodSalesSnapshot ? { salesTotal: vm.previousPeriodSalesSnapshot.salesTotal, expensesTotal: 0, grossMargin: 0 } : null,
    vm.previousPeriodRentabilidadKpis?.rentabilidad
  );

  return (
    <ScrollView
      style={styles.reportsContentScroll}
      contentContainerStyle={[styles.content, { flexGrow: 1, paddingBottom: spacing.xxl }]}
      accessibilityState={{ busy: periodDataLoading }}
      refreshControl={
        <RefreshControl refreshing={vm.refreshing} onRefresh={vm.onRefresh} colors={[ticketColors.ink2]} tintColor={ticketColors.ink2} />
      }
    >
      {ventasSourceHint ? (
        <Text style={[styles.caption, { marginBottom: spacing.sm, color: ticketColors.gold }]}>{ventasSourceHint}</Text>
      ) : null}

      <ReportsScopeFiltersPanel
        vm={vm}
        disabled={periodDataLoading || vm.salesByProductLoading}
        periodAccessibilityPrefix="Período"
      />

      <PaperSheet padded style={[styles.kpiSheet, periodDataLoading && styles.ventasStaleData]} testID="informes.kpi.sheet">
        <Text style={styles.kpiSheetTitle}>Números del período</Text>
        <Text style={styles.kpiSheetHint}>{vm.dateRangeLabel}{periodDataLoading ? ' · actualizando…' : ''}</Text>
        <View style={styles.ventasIndicatorsGrid}>
          <ReportsMetricTile
            layout={tileLayout}
            label="Facturación"
            value={`${formatMoney(currentSales)}`}
            valueTone="emphasis"
            sub={`${periodSalesCount} ventas${
              ticketPromedio > 0
                ? ` · Ticket promedio ${formatMoney(Math.round(ticketPromedio))}`
                : ''
            }`}
            onPress={periodSalesCount > 0 ? () => vm.openBranchSalesHistory() : undefined}
            accessibilityLabel="Ver ventas del período"
          />
          <ReportsMetricTile
            layout={tileLayout}
            label="Ventas fiscales"
            value={`${formatMoney(vm.salesFiscalTotal)}`}
            sub={`${vm.salesFiscalCount} ventas`}
          />
          <ReportsMetricTile
            layout={tileLayout}
            label="Ventas no fiscales"
            value={`${formatMoney(vm.salesNonFiscalTotal)}`}
            sub={`${vm.salesNonFiscalCount} ventas`}
          />
          <ReportsMetricTile
            layout={tileLayout}
            label="Pagos pendientes"
            value={String(pendingPaymentsCount)}
            sub={
              pendingPaymentsCount > 0 ? 'Ver pendientes →' : 'Sin saldo por cobrar'
            }
            subAccent={pendingPaymentsCount > 0}
            onPress={() => vm.openBranchSalesHistoryPending()}
            disabled={pendingPaymentsCount === 0}
            accessibilityLabel="Ver ventas con cobro pendiente"
          />
          <ReportsMetricTile
            layout={tileLayout}
            label="Cobrado estimado"
            value={`${formatMoney(collectedAmount)}`}
          />
          <ReportsMetricTile
            layout={tileLayout}
            label="Saldo pendiente"
            value={`${formatMoney(pendingAmount)}`}
            valueTone={pendingAmount > 0 ? 'warning' : 'default'}
            sub={pendingAmount > 0 ? 'Monto adeudado del período' : 'Sin saldo pendiente'}
            subWarning={pendingAmount > 0}
            warning={pendingAmount > 0}
          />
        </View>
      </PaperSheet>

      {bandsSingleDay ? (
        <View style={periodDataLoading ? styles.ventasStaleData : undefined}>
          <PaperSheet padded>
            <Text style={styles.reportsDenseBlockTitle}>Ventas por franja</Text>
            <Text style={styles.caption}>Por hora, en el día que elegiste.</Text>
            {vm.timeBands.length > 0 ? (
              vm.timeBands.map((band, idx) => (
                <View
                  key={band.band}
                  style={[
                    styles.reportsDenseListFieldRow,
                    idx > 0 && { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: ticketColors.rule, paddingTop: spacing.xs },
                  ]}
                >
                  <Text style={styles.reportsDenseListFieldLabel}>{band.label}</Text>
                  <Text style={styles.reportsDenseListFieldValue}>
                    {formatMoney(band.total)} ({band.count})
                  </Text>
                </View>
              ))
            ) : (
            <Text style={styles.caption}>No hay ventas en las franjas de este día.</Text>
            )}
          </PaperSheet>
        </View>
      ) : null}

      <View style={styles.reportsVentasSectionDivider} />

      <PaperSheet padded style={[styles.kpiSheet, periodDataLoading && styles.ventasStaleData]}>
        <Text style={styles.kpiSheetTitle}>Cómo cobraste</Text>
        {paymentBreakdown.length > 0 ? (
          <ReportsPaymentMixDonut
              items={paymentBreakdown}
              methodNames={vm.paymentMethodNames}
              total={paymentBreakdown.reduce((sum, item) => sum + item.total, 0)}
            />
        ) : (
          <Text style={styles.caption}>No hay cobros en este período.</Text>
        )}
      </PaperSheet>

      <PaperSheet
        padded
        style={[styles.kpiSheet, periodDataLoading && styles.ventasStaleData]}
        testID="reports.ventas.promos.section"
      >
        <Text style={styles.kpiSheetTitle}>Promociones y descuentos</Text>
        <View style={styles.ventasIndicatorsGrid}>
          <ReportsMetricTile
            layout={tileLayoutAdaptive}
            label="Descuento total"
            value={`${formatMoney(discountTotal)}`}
            valueTone="emphasis"
          />
          <ReportsMetricTile
            layout={tileLayoutAdaptive}
            label="Tasa de descuento"
            value={`${(discountRate * 100).toFixed(1)}%`}
            valueTone="emphasis"
          />
          <ReportsMetricTile
            layout={tileLayoutAdaptive}
            label="Tickets con promo"
            value={String(salesWithDiscountCount)}
          />
          <ReportsMetricTile
            layout={tileLayoutAdaptive}
            label="Tickets sin promo"
            value={String(salesWithoutDiscountCount)}
          />
          <ReportsMetricTile
            layout={tileLayoutAdaptive}
            label="Promedio con promo"
            value={formatMoney(Math.round(avgDiscountPerSaleWithPromo))}
          />
        </View>
        {vm.promotionPerformance && vm.promotionPerformance.topPromotions.length > 0 ? (
          <View style={styles.reportsDenseListSection} testID="reports.ventas.promos.top">
            <Text style={styles.reportsSheetBlockTitle}>Promos con más descuento</Text>
            {vm.promotionPerformance.topPromotions.map((row, index) => (
              <View key={`${row.promotionId ?? row.promotionLabel}:${index}`} style={styles.reportsDenseListFieldRow}>
                <Text style={styles.reportsDenseListFieldLabel} numberOfLines={2}>
                  {index + 1}. {row.promotionLabel}
                </Text>
                <Text style={styles.reportsDenseListFieldValue}>
                  {formatMoney(row.discountTotal)} · {row.usesCount} usos
                </Text>
              </View>
            ))}
          </View>
        ) : (
          <Text style={styles.caption} testID="reports.ventas.promos.empty">
            No hay promos en este rango.
          </Text>
        )}
      </PaperSheet>

      <View style={styles.reportsVentasSectionDivider} />

      <PaperSheet padded style={[styles.kpiSheet, periodDataLoading && styles.ventasStaleData]}>
        <Text style={styles.kpiSheetTitle}>Rentabilidad de productos</Text>
        <Text style={[styles.caption, { color: ticketColors.ink3, marginTop: 2 }]}>
          Margen y resultado neto. Para plata cobrada y pagada, mirá Resumen → Plata del período.
        </Text>
        {vm.periodRentabilidadKpis ? (
          <>
          <View style={[styles.ventasIndicatorsGrid, { marginTop: spacing.sm }]}>
            <ReportsMetricTile
              layout={tileLayoutAdaptive}
              label="Margen estimado"
              value={`${formatMoney(vm.periodRentabilidadKpis.rentabilidad.margenReposicionTotal)}`}
              valueEmphasis
              valueTone={
                (vm.periodRentabilidadKpis.rentabilidad.margenReposicionTotal ?? 0) >= 0
                  ? 'positive'
                  : 'negative'
              }
              valueNumberOfLines={1}
            />
            <ReportsMetricTile
              layout={tileLayoutAdaptive}
              label="Costo reposición"
              value={`${formatMoney(vm.periodRentabilidadKpis.rentabilidad.costoReposicionTotal)}`}
              valueTone="emphasis"
              sub="Productos físicos"
              valueNumberOfLines={1}
              subNumberOfLines={2}
            />
            <ReportsMetricTile
              layout={tileLayoutAdaptive}
              label="Rentabilidad"
              value={`${(vm.periodRentabilidadKpis.rentabilidad.rentabilidadReposicionPct * 100).toFixed(1)}%`}
              valueTone="emphasis"
              valueNumberOfLines={1}
            />
            {expensesModuleEnabled && profitLoss ? (
              <>
                <ReportsMetricTile
                  layout={tileLayoutAdaptive}
                  label="Gastos"
                  value={`${formatMoney(fullPl.expensesTotal)}`}
                  valueTone={fullPl.expensesTotal > 0 ? 'warning' : 'default'}
                  valueNumberOfLines={1}
                />
                <ReportsMetricTile
                  layout={tileLayoutAdaptive}
                  label="Resultado neto"
                  value={`${formatMoney(fullPl.netIncome)}`}
                  valueTone={fullPl.netIncome >= 0 ? 'positive' : 'negative'}
                  sub={`${(fullPl.netIncomePct * 100).toFixed(1)}% neto${formatDelta(safeDeltaPct(fullPl.netIncome, prevPl.netIncome))}`}
                  valueNumberOfLines={1}
                />
              </>
            ) : null}
          </View>
          {vm.periodRentabilidadKpis.rentabilidadDaily.length > 0 ? (
            <View style={{ marginTop: spacing.sm }}>
              <ReportsMarginDailyBars rows={vm.periodRentabilidadKpis.rentabilidadDaily} rangeDays={daysInRange} />
            </View>
          ) : null}
          </>
          ) : vm.rentabilidadError ? (
            <ReportsErrorRetryCard
              message={vm.rentabilidadError}
              showRetry={vm.rentabilidadErrorShowRetry}
              onRetry={() => void vm.handleLoadRentabilidad()}
              retryAccessibilityLabel="Reintentar cargar margen"
            />
          ) : vm.rentabilidadLoading || periodDataLoading ? (
            <Text style={[styles.caption, { marginTop: spacing.sm, color: ticketColors.ink3 }]}>
              Cargando margen…
            </Text>
          ) : (
            <Text style={[styles.caption, { marginTop: spacing.sm, color: ticketColors.ink3 }]}>
              Sin datos de margen ni ganancia. Conectate y recargá.
            </Text>
          )}
      </PaperSheet>

      {vm.previousPeriodSalesSnapshot ? (
        <PaperSheet padded style={[styles.kpiSheet, periodDataLoading && styles.ventasStaleData]}>
          <Text style={styles.kpiSheetTitle}>Contra el período anterior</Text>
          <Text style={styles.kpiSheetHint}>Respecto de {previousLabel}</Text>
            <View style={styles.ventasIndicatorsGrid}>
              <ReportsMetricTile
                layout={tileLayoutAdaptive}
                label={`Ventas · ${previousLabel}`}
                value={`${formatMoney(previousSales)}`}
                valueTone="emphasis"
              />
              <ReportsMetricTile
                layout={tileLayoutAdaptive}
                label="Variación vs anterior"
                value={`${salesDelta >= 0 ? '+' : '-'}${formatMoney(Math.abs(salesDelta))}${
                  salesDeltaPct == null ? ' · n/d' : ` · ${(salesDeltaPct * 100).toFixed(1)}%`
                }`}
                valueTone={salesDelta > 0 ? 'positive' : salesDelta < 0 ? 'negative' : 'default'}
                valueNumberOfLines={2}
              />
              <ReportsMetricTile
                layout={tileLayoutAdaptive}
                label="Promedio diario"
                value={formatMoney(Math.round(currentSalesPerDay))}
                valueTone="emphasis"
                sub={`vs ${formatMoney(Math.round(previousSalesPerDay))} en ${previousLabel}`}
                valueNumberOfLines={1}
                subNumberOfLines={2}
              />
              {vm.dateRangePreset === 'today' && vm.salesComparison ? (
                <ReportsMetricTile
                  layout={tileLayoutAdaptive}
                  label="Mismo día semana pasada"
                  value={`${formatMoney(vm.salesComparison.sameDayLastWeekTotal)}`}
                  valueTone="emphasis"
                  sub={`${vm.salesComparison.sameDayLastWeekCount} ventas`}
                />
              ) : null}
              {vm.previousPeriodRentabilidadKpis && vm.periodRentabilidadKpis ? (
                <>
                  <ReportsMetricTile
                    layout={tileLayoutAdaptive}
                    label={`Margen · ${previousLabel}`}
                    value={`${formatMoney(vm.previousPeriodRentabilidadKpis.rentabilidad.margenReposicionTotal)}`}
                    valueTone="emphasis"
                  />
                  <ReportsMetricTile
                    layout={tileLayoutAdaptive}
                    label="Variación margen"
                    value={`${marginDelta >= 0 ? '+' : '-'}${formatMoney(Math.abs(marginDelta))}${
                      marginDeltaPct == null ? ' · n/d' : ` · ${(marginDeltaPct * 100).toFixed(1)}%`
                    }`}
                    valueTone={marginDelta > 0 ? 'positive' : marginDelta < 0 ? 'negative' : 'default'}
                    valueNumberOfLines={2}
                  />
                </>
              ) : null}
            </View>
        </PaperSheet>
        ) : (
        <PaperSheet padded style={[styles.kpiSheet, periodDataLoading && styles.ventasStaleData]}>
          <Text style={styles.kpiSheetTitle}>Contra el período anterior</Text>
          <Text style={styles.caption}>No hay datos del período anterior para comparar. Conectate y recargá.</Text>
        </PaperSheet>
      )}

      {vm.salesDailySeries && vm.salesDailySeries.length > 0 ? (
        <View style={periodDataLoading ? styles.ventasStaleData : undefined}>
          <PaperSheet padded>
            <ReportsSalesDailyBars rows={vm.salesDailySeries} rangeDays={daysInRange} />
          </PaperSheet>
        </View>
      ) : !periodDataLoading ? (
        <PaperSheet padded>
          <Text style={styles.kpiSheetTitle}>Ventas por día</Text>
          <Text style={styles.caption}>No hay datos diarios en este período.</Text>
        </PaperSheet>
      ) : null}

      <View style={styles.reportsVentasSectionDivider} />

      <ReportsBlockHeading label="Ventas por ítem" />

      <View style={periodDataLoading ? styles.ventasStaleData : undefined}>
        <ReportsSalesByProductSection vm={vm} dateRangeLabel={vm.dateRangeLabel} periodDataLoading={periodDataLoading} daysInRange={daysInRange} />
      </View>

      <View style={styles.reportsVentasSectionDivider} />

      <ReportsDataSourceFooter lastUpdatedAt={vm.lastDashboardLoadAt} dataSourceHint={ventasSourceHint || undefined} />
    </ScrollView>
  );
}
